import { getCurrentConfig } from '../config';
import { API_ENDPOINTS, DEFAULT_HEADERS } from '../config/constants';
import { universalApiService } from './universalApiService';
import { logger } from '../utils/logging/logger';
import {
  DashboardMasterData,
  DashboardMasterResponse,
  DashboardHashCheckResponse,
  ComputedDashboardStats,
  CheckpointGridItem,
  LaneGridItem,
  CheckpointDataDetail,
  LaneAssignmentDataDetail
} from '../types/dashboardMaster';

class DashboardMasterService {
  private cachedData: DashboardMasterData | null = null;
  private currentHash: string | null = null;
  private subscribers: ((data: DashboardMasterData) => void)[] = [];
  private periodicUpdateInterval: NodeJS.Timeout | null = null;

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    // Use the universal API service that follows the exact same pattern as the working dashboard API
    return universalApiService.get<T>(endpoint);
  }

  // Subscribe to data changes
  subscribe(callback: (data: DashboardMasterData) => void): () => void {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(sub => sub !== callback);
    };
  }

  // Notify all subscribers
  private notifySubscribers(data: DashboardMasterData): void {
    this.subscribers.forEach(callback => callback(data));
  }

  // Get dashboard master data
  async getDashboardMaster(forceRefresh = false): Promise<DashboardMasterData> {
    try {
      logger.api('Dashboard Master Service: Fetching data from primary endpoint');
      
      let response: DashboardMasterResponse;
      
      try {
        // Try primary endpoint first
        response = await this.request<DashboardMasterResponse>(API_ENDPOINTS.DASHBOARD);
      } catch (primaryError) {
        // Dashboard Master Service: Primary endpoint failed, throw error
        throw primaryError;
      }
      
      logger.api('Dashboard Master Service: Response received', {
        success: response.success,
        hasData: !!response.data,
        dataType: typeof response.data,
        terminalsCount: response.data?.terminals?.length || 0,
        checkpointsCount: response.data?.terminals?.flatMap(t => t.checkpoints || [])?.length || 0
      });
      
      if (response.success && response.data) {
        this.cachedData = response.data;
        this.currentHash = response.data.hash;
        this.notifySubscribers(response.data);
        
        logger.api('Dashboard Master Service: Data cached successfully', {
          hash: response.data.hash,
          terminals: response.data.terminals?.length || 0,
          totalCheckpoints: response.data.terminals?.flatMap(t => t.checkpoints || [])?.length || 0
        });
        
        return response.data;
      } else {
        console.error('🔍 Dashboard Master Service: API returned error', response.error);
        throw new Error(response.error || 'Failed to fetch dashboard data');
      }
    } catch (error) {
      console.error('🔍 Dashboard Master Service: Request failed', error);
      throw error;
    }
  }

  // Check for updates using hash
  async checkForUpdates(): Promise<boolean> {
    if (!this.currentHash) return true;

    try {
      const response = await this.request<DashboardHashCheckResponse>(
        API_ENDPOINTS.DASHBOARD_WITH_HASH(this.currentHash)
      );

      if (response.success && response.data) {
        return response.data.status === 'changed';
      }
      return true;
    } catch (error) {
      return true; // Assume changed if we can't check
    }
  }
  // Force refresh all data (uses hash-based refresh for efficiency)
  async forceRefresh(): Promise<DashboardMasterData> {
    // Clear cached data and hash to force full refresh
    this.cachedData = null;
    this.currentHash = null;
    
    // Do full refresh
    return this.getDashboardMaster(true);
  }

  // Get computed statistics from cached data
  getComputedStats(): ComputedDashboardStats | null {
    if (!this.cachedData) return null;

    const terminals = this.cachedData.terminals;
    const allCheckpoints = terminals.flatMap(t => t.checkpoints);
    const allSections = allCheckpoints.flatMap(c => c.sections);
    const allAssets = allCheckpoints.flatMap(c => c.assets);
    const allLaneAssignments = allCheckpoints.flatMap(c => c.laneassignments);
    const allAssetGroups = allLaneAssignments.flatMap(la => la.assetgroups);

    return {
      terminals: {
        total: terminals.length,
        active: terminals.filter(t => t.isActive).length,
        open: terminals.filter(t => t.isOpen).length,
      },
      checkpoints: {
        total: allCheckpoints.length,
        active: allCheckpoints.filter(c => c.isActive).length,
        open: allCheckpoints.filter(c => c.isOpen).length,
        closed: allCheckpoints.filter(c => !c.isOpen).length,
        withExceptions: allCheckpoints.filter(c => c.isException).length,
      },
      sections: {
        total: allSections.length,
        active: allSections.filter(s => s.isActive).length,
        open: allSections.filter(s => s.isOpen).length,
      },
      assets: {
        total: allAssets.length,
        active: allAssets.filter(a => a.isActive).length,
        open: allAssets.filter(a => a.isOpen).length,
        closed: allAssets.filter(a => !a.isOpen).length,
      },
      laneAssignments: {
        total: allLaneAssignments.length,
        active: allLaneAssignments.filter(la => la.is_active).length,
        open: allLaneAssignments.filter(la => la.isOpen).length,
      },
      assetGroups: {
        total: allAssetGroups.length,
        active: allAssetGroups.filter(ag => ag.is_active).length,
        open: allAssetGroups.filter(ag => ag.isOpen).length,
      },
    };
  }
  // Get checkpoint grid data for tables/grids
  getCheckpointGridData(): CheckpointGridItem[] {
    if (!this.cachedData) return [];

    return this.cachedData.terminals.flatMap(terminal =>
      terminal.checkpoints.map(checkpoint => ({
        id: checkpoint.id,
        name: checkpoint.name,
        description: checkpoint.description,
        isOpen: checkpoint.isOpen,
        isActive: checkpoint.isActive,
        isException: checkpoint.isException,
        exception_end_time: checkpoint.exception_end_time,
        terminalName: terminal.name,
        sectionsCount: checkpoint.sections.length,
        assetsCount: checkpoint.assets.length,
        laneAssignmentsCount: checkpoint.laneassignments.length,
        last_updated: checkpoint.last_updated,
        updated_by: checkpoint.updated_by,
      }))
    );
  }

  // Get lane assignment grid data for tables/grids
  getLaneGridData(): LaneGridItem[] {
    if (!this.cachedData) return [];

    return this.cachedData.terminals.flatMap(terminal =>
      terminal.checkpoints.flatMap(checkpoint =>
        checkpoint.laneassignments.map(laneAssignment => {
          const totalAssets = laneAssignment.assetgroups.reduce(
            (sum, ag) => sum + ag.assets.length, 0
          );
          const openAssets = laneAssignment.assetgroups.reduce(
            (sum, ag) => sum + ag.assets.filter(a => a.isOpen).length, 0
          );

          return {
            laneAssignmentId: laneAssignment.id,
            checkpointId: checkpoint.id,
            checkpointName: checkpoint.name,
            terminalName: terminal.name,
            laneTypeName: laneAssignment.lanetype.type_name,
            laneTypeDescription: laneAssignment.lanetype.description,
            laneTypePurpose: laneAssignment.lanetype.purpose,
            isOpen: laneAssignment.isOpen,
            isActive: laneAssignment.is_active,
            assetGroupsCount: laneAssignment.assetgroups.length,
            totalAssets,
            openAssets,
            last_updated: laneAssignment.last_updated,
            updated_by: laneAssignment.updated_by,
          };
        })
      )
    );
  }
  // Get specific checkpoint data
  getCheckpointData(checkpointId: number): CheckpointDataDetail | null {
    if (!this.cachedData) return null;

    for (const terminal of this.cachedData.terminals) {
      const checkpoint = terminal.checkpoints.find(c => c.id === checkpointId);
      if (checkpoint) {
        // Calculate computed stats for this checkpoint
        const computedStats = {
          sectionsCount: checkpoint.sections.length,
          assetsCount: checkpoint.assets.length,
          laneAssignmentsCount: checkpoint.laneassignments.length,
          openSections: checkpoint.sections.filter(s => s.isOpen).length,
          openAssets: checkpoint.assets.filter(a => a.isOpen).length,
          openLaneAssignments: checkpoint.laneassignments.filter(la => la.isOpen).length,
        };

        return {
          checkpoint,
          terminal: {
            id: terminal.id,
            name: terminal.name,
            description: terminal.description,
            isActive: terminal.isActive,
            isOpen: terminal.isOpen,
          },
          computedStats,
        };
      }
    }
    return null;
  }

  // Get specific lane assignment data
  getLaneAssignmentData(laneAssignmentId: number): LaneAssignmentDataDetail | null {
    if (!this.cachedData) return null;

    for (const terminal of this.cachedData.terminals) {
      for (const checkpoint of terminal.checkpoints) {
        const laneAssignment = checkpoint.laneassignments.find(la => la.id === laneAssignmentId);
        if (laneAssignment) {
          // Calculate computed stats for this lane assignment
          const totalAssets = laneAssignment.assetgroups.reduce(
            (sum, ag) => sum + ag.assets.length, 0
          );
          const openAssets = laneAssignment.assetgroups.reduce(
            (sum, ag) => sum + ag.assets.filter(a => a.isOpen).length, 0
          );
          const openAssetGroups = laneAssignment.assetgroups.filter(ag => ag.isOpen).length;

          const computedStats = {
            assetGroupsCount: laneAssignment.assetgroups.length,
            totalAssets,
            openAssets,
            openAssetGroups,
          };

          return {
            laneAssignment,
            checkpoint: {
              id: checkpoint.id,
              name: checkpoint.name,
              description: checkpoint.description,
              isOpen: checkpoint.isOpen,
              isActive: checkpoint.isActive,
              isException: checkpoint.isException,
            },
            terminal: {
              id: terminal.id,
              name: terminal.name,
              description: terminal.description,
              isActive: terminal.isActive,
              isOpen: terminal.isOpen,
            },
            computedStats,
          };
        }
      }
    }
    return null;
  }

  // Start periodic updates
  startPeriodicUpdates(intervalMinutes = 2): () => void {
    // Clear existing interval if any
    if (this.periodicUpdateInterval) {
      clearInterval(this.periodicUpdateInterval);
    }

    this.periodicUpdateInterval = setInterval(async () => {
      try {
        const hasUpdates = await this.checkForUpdates();
        if (hasUpdates) {
          await this.getDashboardMaster();
        }
      } catch (error) {
        // Silent fail for periodic updates
      }
    }, intervalMinutes * 60 * 1000);

    // Return stop function
    return () => {
      if (this.periodicUpdateInterval) {
        clearInterval(this.periodicUpdateInterval);
        this.periodicUpdateInterval = null;
      }
    };
  }
  // Stop periodic updates
  stopPeriodicUpdates(): void {
    if (this.periodicUpdateInterval) {
      clearInterval(this.periodicUpdateInterval);
      this.periodicUpdateInterval = null;
    }
  }

  // Clear cached data
  clearCache(): void {
    this.cachedData = null;
    this.currentHash = null;
  }

  // Get current hash
  getCurrentHash(): string | null {
    return this.currentHash;
  }

  // Get cached data
  getCachedData(): DashboardMasterData | null {
    return this.cachedData;
  }
}

// Export singleton instance
export const dashboardMasterService = new DashboardMasterService();
export default dashboardMasterService;